import React, { useEffect, useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

/**
 * PWA Web Share Target handler.
 *
 * The manifest's `share_target` block routes incoming OS-share intents to
 * this component with title/text/url query params. We:
 *   1) Try to extract lat/lng coordinates from the payload.
 *   2) Redirect the user to /dashboard, passing recognised data so the
 *      Map can center on it or drop a pin.
 *
 * Recognised payload shapes
 *   • Google Maps share URLs:  https://maps.app.goo.gl/... or /@lat,lng,zoom
 *   • Apple Maps:              https://maps.apple.com/?ll=lat,lng
 *   • Plain "lat,lng"          e.g. text="37.7749,-122.4194"
 *   • Geo URI                  geo:37.7749,-122.4194
 * Anything else lands the user on the dashboard with a toast showing the
 * received text so they can react manually (e.g. a shared contact name).
 */
function parseCoords(...candidates) {
  const patterns = [
    /@(-?\d+(?:\.\d+)?)[, ]+(-?\d+(?:\.\d+)?)/,          // ...@lat,lng[,zoom]
    /[?&]ll=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,        // Apple ll=
    /[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,         // ?q=lat,lng
    /geo:(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,           // geo:lat,lng
    /^\s*(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/, // "lat,lng"
  ];
  for (const s of candidates) {
    if (!s) continue;
    for (const p of patterns) {
      const m = s.match(p);
      if (m) {
        const lat = parseFloat(m[1]);
        const lng = parseFloat(m[2]);
        if (
          Number.isFinite(lat) && Number.isFinite(lng) &&
          Math.abs(lat) <= 90 && Math.abs(lng) <= 180
        ) return { lat, lng };
      }
    }
  }
  return null;
}

export default function ShareTarget() {
  const [params] = useSearchParams();
  const title = params.get("title") || "";
  const text = params.get("text") || "";
  const url = params.get("url") || "";

  const coords = useMemo(() => parseCoords(url, text, title), [url, text, title]);

  useEffect(() => {
    if (coords) {
      toast.success("Shared location received", {
        description: `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)} — centering the map…`,
      });
    } else if (text || url || title) {
      toast.message("Shared to Proximity", {
        description: (title || text || url).slice(0, 140),
      });
    }
  }, [coords, title, text, url]);

  // Redirect to Dashboard with recognised data as query params.
  const search = new URLSearchParams();
  if (coords) {
    search.set("lat", String(coords.lat));
    search.set("lng", String(coords.lng));
    search.set("source", "share");
  }
  if (title) search.set("share_title", title);
  if (text)  search.set("share_text", text.slice(0, 500));
  if (url)   search.set("share_url", url.slice(0, 500));

  return <Navigate to={`/dashboard?${search.toString()}`} replace />;
}
